import { c as _c } from "react/compiler-runtime";
import { useStore } from 'zustand';
import { useMemo } from 'react';
import { useKeyboard } from '@/hooks/keyboard/use-keyboard';
import { useAnalyzedKeyboard } from '@/hooks/keyboard/use-analyzed-keyboard';
import { useDesignedKeyboard } from '@/hooks/keyboard/use-designed-keyboard';
import { useIsDesignCompleted } from '@/hooks/keyboard/use-design-completion';
import { useMeasuredKeyCount } from '@/hooks/keyboard/use-measured-key-count';
import { useFrontWeightDesign, useStrikeWeightDesign, useStrikeWeightRatioDesign, useWippenSupportSpringsDesign } from '@/hooks/store/use-design-store';
import { FrontWeightDesignMode } from '@/lib/piano/touch-design/design/front-weight-design';
import { StrikeWeightDesignMode } from '@/lib/piano/touch-design/design/strike-weight-design';
import { StrikeWeightRatioDesignMode } from '@/lib/piano/touch-design/design/strike-weight-ratio-design';
import { WippenSupportSpringsDesignMode } from '@/lib/piano/touch-design/design/wippen-support-springs-design';
import { usePianoProfileState } from '@/hooks/store/use-piano-profile-store';
import { useMeasuresStore, type MeasuresStore } from '@/hooks/store/use-measure-store';
import { useShallow } from 'zustand/shallow';
import type { TouchWeightAnalyzedKeyboard } from '@/lib/piano/touch-design/touch-weight-key-analysis';

/**
 * How far the reader has got, which is what the home page opens on.
 *
 * `demo` is the instrument the application loads on a first visit; the three
 * others are the stages of someone's own piano. Nothing of this is stored:
 * the stage follows from the measurements and the design, so it can never
 * fall out of step with them.
 */
export type HomeStage = 'demo' | 'measuring' | 'measured' | 'designed';
export interface HomeProgress {
  /** One of the four design parameters asks for something other than "as is". */
  hasDesign: boolean;
  isDemoProfile: boolean;
  isDesignCompleted: boolean;
  measured: number;
  total: number;
}

/** The stage a reader is at, from how far their piano has got. */
export const homeStageOf = ({
  hasDesign,
  isDemoProfile,
  isDesignCompleted,
  measured,
  total
}: HomeProgress): HomeStage => isDemoProfile ? 'demo' : measured < total ? 'measuring' : hasDesign && isDesignCompleted ? 'designed' : 'measured';

/** Where the main call to action leads from each stage: what is left to do. */
export const RESUME_TO: Record<HomeStage, string> = {
  // The demo is already loaded: the button opens it rather than reloading.
  demo: '/analyze/touch-weight',
  designed: '/design/adjustment-sheet',
  // The first step of the design. Springs are designed whether or not they
  // were measured: skipping the step with the option off hid it from whoever
  // has springs and never measured them.
  measured: '/design/wippen-support-springs',
  measuring: '/measure'
};
export interface HomeState {
  analyzedKeyboard: TouchWeightAnalyzedKeyboard;
  designedKeyboard: TouchWeightAnalyzedKeyboard;
  /** Keys carrying both a down and an up weight. */
  measured: number;
  /** The first key still missing one of the two, for "next up". */
  nextKeyToMeasure: {
    name: string;
    number: number;
  } | null;
  /** Where the main call to action leads: the next thing left to do. */
  resumeTo: string;
  stage: HomeStage;
  total: number;
}
export const useHomeState = () => {
  const $ = _c(22);
  const {
    isDemoProfile
  } = usePianoProfileState();
  const {
    keyboard
  } = useKeyboard();
  const {
    measured,
    total
  } = useMeasuredKeyCount();
  const analyzedKeyboard = useAnalyzedKeyboard();
  const designedKeyboard = useDesignedKeyboard();
  const isDesignCompleted = useIsDesignCompleted(designedKeyboard);
  const {
    frontWeightDesignMode
  } = useFrontWeightDesign();
  const {
    strikeWeightDesignMode
  } = useStrikeWeightDesign();
  const {
    strikeWeightRatioDesignMode
  } = useStrikeWeightRatioDesign();
  const {
    wippenSupportSpringsDesignMode
  } = useWippenSupportSpringsDesign();
  const hasDesign = frontWeightDesignMode !== FrontWeightDesignMode.AsMeasured || strikeWeightDesignMode !== StrikeWeightDesignMode.AsMeasured || strikeWeightRatioDesignMode !== StrikeWeightRatioDesignMode.AsMeasured || wippenSupportSpringsDesignMode !== WippenSupportSpringsDesignMode.None;
  const measures = useStore(useMeasuresStore(), useShallow(_temp));
  let t0;
  bb0: {
    let t1;
    if ($[0] !== keyboard.size || $[1] !== measures) {
      t1 = measures.slice(0, keyboard.size).findIndex(_temp2);
      $[0] = keyboard.size;
      $[1] = measures;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    const index = t1;
    if (index === -1) {
      t0 = null;
      break bb0;
    }
    let t2;
    if ($[3] !== index || $[4] !== keyboard) {
      t2 = keyboard.getKeyByNumber(index + 1);
      $[3] = index;
      $[4] = keyboard;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    const key_0 = t2;
    let t3;
    if ($[6] !== key_0) {
      t3 = key_0 ? {
        name: key_0.name,
        number: key_0.number
      } : null;
      $[6] = key_0;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    t0 = t3;
  }
  const nextKeyToMeasure = t0;
  let t1;
  if ($[8] !== hasDesign || $[9] !== isDemoProfile || $[10] !== isDesignCompleted || $[11] !== measured || $[12] !== total) {
    t1 = homeStageOf({
      hasDesign,
      isDemoProfile,
      isDesignCompleted,
      measured,
      total
    });
    $[8] = hasDesign;
    $[9] = isDemoProfile;
    $[10] = isDesignCompleted;
    $[11] = measured;
    $[12] = total;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  const stage = t1;
  const t2 = RESUME_TO[stage];
  let t3;
  if ($[14] !== analyzedKeyboard || $[15] !== designedKeyboard || $[16] !== measured || $[17] !== nextKeyToMeasure || $[18] !== stage || $[19] !== t2 || $[20] !== total) {
    t3 = {
      analyzedKeyboard,
      designedKeyboard,
      measured,
      nextKeyToMeasure,
      resumeTo: t2,
      stage,
      total
    };
    $[14] = analyzedKeyboard;
    $[15] = designedKeyboard;
    $[16] = measured;
    $[17] = nextKeyToMeasure;
    $[18] = stage;
    $[19] = t2;
    $[20] = total;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  return t3;
};
function _temp(state) {
  return state.keys;
}
function _temp2(key) {
  return key.downWeightWithoutSpringSupport === null || key.upWeight === null;
}